import {AnchorButton, Button as BJSButton, Icon as BJSIcon, IconName} from '@blueprintjs/core';
import {ButtonConfig} from '@mintdata/mint-components/components/button/ButtonConfig';
import {ButtonConverter} from '@mintdata/mint-components/components/button/ButtonConverter';
import {ButtonModel} from '@mintdata/mint-components/components/button/ButtonModel';
import {ButtonType} from '@mintdata/mint-components/components/button/ButtonType';
import {LinkTarget} from '@mintdata/mint-components/components/button/LinkTarget';
import '@mintdata/mint-components/styles/blueprint.less';
import {BJSUtil} from '@mintdata/mint-components/util/BJSUtil';
import {AnimateProperty, AnimateRelatedProperty} from '@mintdata/mint-kit/api/animation/AnimationProps';
import {
    BaseComponentWithStateContext,
    componentStateContextTypes
} from '@mintdata/mint-kit/api/components/BaseComponentWithStateContext';
import {Component} from '@mintdata/mint-kit/api/decorators/Component';
import {Event} from '@mintdata/mint-kit/api/decorators/Event';
import {Property} from '@mintdata/mint-kit/api/decorators/Property';
import {AnEvent} from '@mintdata/mint-kit/api/events/EventFn';
import {AFocusEvent, AMouseEvent} from '@mintdata/mint-kit/api/events/EventType';
import {ComponentState} from '@mintdata/mint-kit/api/models/componentState/ComponentState';
import {TextAlign} from '@mintdata/mint-kit/api/models/TextAlign';
import {TextVerticalAlign} from '@mintdata/mint-kit/api/models/TextVerticalAlign';
import {BaseProps} from '@mintdata/mint-kit/api/properties/BaseProps';
import {PropertyCategory} from '@mintdata/mint-kit/api/properties/PropertyCategory';
import {PropertyPanelTabId} from '@mintdata/mint-kit/api/properties/PropertyPanelTabId';
import bind from 'bind-decorator';
import * as classnames from 'classnames/bind';
import 'dom4';
import {styler} from 'popmotion/dist/popmotion.js';
import * as React from 'react';
import {Styler} from 'stylefire';

const styles = require('@mintdata/mint-components/components/button/Button.less');
const cx = classnames.bind(styles);

export class ButtonProps extends BaseProps
{
    @Property<ButtonModel>({
        title: '',
        tip: '',
        category: PropertyCategory.Appearance,
        isRequired: false,
        typeConfig: ButtonConfig,
        typeCtor: ButtonModel
    })
    model?:ButtonModel = ButtonModel.default();

    @Event({isEnabled: true, title: 'onClick', tip: 'Click'})
    onClick?:AnEvent<AMouseEvent>;

    @Event({title: 'onDoubleClick', tip: 'Double click'})
    onDoubleClick?:AnEvent<AMouseEvent>;

    @Event({title: 'onMouseDown', tip: 'Mouse down'})
    onMouseDown?:AnEvent<AMouseEvent>;

    @Event({title: 'onMouseUp', tip: 'Mouse up'})
    onMouseUp?:AnEvent<AMouseEvent>;

    @Event({title: 'onMouseMove', tip: 'Mouse move'})
    onMouseMove?:AnEvent<AMouseEvent>;

    @Event({title: 'onMouseOver', tip: 'Mouse over'})
    onMouseOver?:AnEvent<AMouseEvent>;

    @Event({title: 'onMouseOut', tip: 'Mouse out'})
    onMouseOut?:AnEvent<AMouseEvent>;

    @Event({title: 'onMouseEnter', tip: 'Mouse enter'})
    onMouseEnter?:AnEvent<AMouseEvent>;

    @Event({title: 'onMouseLeave', tip: 'Mouse leave'})
    onMouseLeave?:AnEvent<AMouseEvent>;

    @Event({title: 'onFocused', tip: 'Focused'})
    onFocused?:AnEvent<AFocusEvent>;

    @Event({title: 'onLostFocus', tip: 'Lost focus'})
    onLostFocus?:AnEvent<AFocusEvent>;
}

interface IButtonState
{
    isHovered?:boolean;
    isActive?:boolean;
}

@Component<ButtonProps>({
    name: 'Button',
    tip: 'Button Component',
    linkNameToProp: 'model.caption',
    props: ButtonProps,
    supportsChildren: false,
    isSvgCompatible: false,
    width: 140,
    height: 38,
    exampleFrame: ButtonModel.exampleFrame()
})
export class Button extends BaseComponentWithStateContext<ButtonProps, IButtonState>
{
    public static readonly childContextTypes = componentStateContextTypes;

    private _buttonRef:HTMLDivElement | null = null;
    private _buttonStyler:Styler = null;

    public constructor()
    {
        super();

        this.state = {
            isHovered: false,
            isActive: false
        };
    }

    @bind
    private _refHandler(ref:HTMLDivElement | null)
    {
        this._buttonRef = ref;
        this._buttonStyler = null;
    };

    @bind
    private _onClick(event:React.MouseEvent<HTMLElement>):void
    {
        const {onClick} = this.props;

        if( onClick )
            onClick(new AMouseEvent(event));
    }

    @bind
    private _onDoubleClick(event:React.MouseEvent<HTMLElement>):void
    {
        const {onDoubleClick} = this.props;

        if( onDoubleClick )
            onDoubleClick(new AMouseEvent(event));
    }

    @bind
    private _onMouseDown(event:React.MouseEvent<HTMLElement>):void
    {
        const {onMouseDown, model: {activeState}, isDraggable} = this.props;

        if( onMouseDown )
            onMouseDown(new AMouseEvent(event));

        if( isDraggable )
            return;

        this.setState({
            isActive: true
        });

        if( activeState.isEnabled.value && !this.context.isParentActiveEventPropagationEnabled )
            event.stopPropagation();
    }

    @bind
    private _onMouseUp(event:React.MouseEvent<HTMLElement>):void
    {
        const {onMouseUp} = this.props;

        this.setState({
            isActive: false
        });

        if( onMouseUp )
            onMouseUp(new AMouseEvent(event));
    }

    @bind
    private _onMouseMove(event:React.MouseEvent<HTMLElement>):void
    {
        const {onMouseMove} = this.props;

        if( onMouseMove )
            onMouseMove(new AMouseEvent(event));
    }

    @bind
    private _onMouseOver(event:React.MouseEvent<HTMLElement>):void
    {
        const {onMouseOver} = this.props;

        if( onMouseOver )
            onMouseOver(new AMouseEvent(event));
    }

    @bind
    private _onMouseOut(event:React.MouseEvent<HTMLElement>):void
    {
        const {onMouseOut} = this.props;

        if( onMouseOut )
            onMouseOut(new AMouseEvent(event));
    }

    @bind
    private _onMouseEnter(event:React.MouseEvent<HTMLElement>):void
    {
        const {onMouseEnter} = this.props;

        this.setState({
            isHovered: true
        });

        if( onMouseEnter )
            onMouseEnter(new AMouseEvent(event));
    }

    @bind
    private _onMouseLeave(event:React.MouseEvent<HTMLElement>):void
    {
        const {onMouseLeave} = this.props;

        this.setState({
            isHovered: false,
            isActive: false
        });

        if( onMouseLeave )
            onMouseLeave(new AMouseEvent(event));
    }

    @bind
    private _onFocused(event:React.FocusEvent<HTMLElement>):void
    {
        const {onFocused} = this.props;

        if( onFocused )
            onFocused(new AFocusEvent(event));
    }

    @bind
    private _onLostFocus(event:React.FocusEvent<HTMLElement>):void
    {
        const {onLostFocus} = this.props;

        if( onLostFocus )
            onLostFocus(new AFocusEvent(event));
    }

    public componentWillMount():void
    {
        const {assets: {fonts: fontsController}, animation} = this.props;
        const {model: {font, hoverState, activeState}} = this.props;

        fontsController.checkAndLoadFont(font);
        fontsController.checkAndLoadFont(hoverState.font);
        fontsController.checkAndLoadFont(activeState.font);

        animation.registerPropStylerFunc(this._getPropStyler);
        animation.registerPropPathFunc(this._getPropPath);
    }

    public componentWillUnmount():void
    {
        const {assets: {fonts: fontsController}} = this.props;
        const {model: {font, hoverState, activeState}} = this.props;

        fontsController.unloadFont(font);
        fontsController.unloadFont(hoverState.font);
        fontsController.unloadFont(activeState.font);
    }

    componentWillReceiveProps(nextProps:Readonly<ButtonProps>, nextContext:any):void
    {
        const {model: newModel} = nextProps;

        const {assets: {fonts: fontsController}} = this.props;
        const {model: {font, hoverState, activeState}} = this.props;

        fontsController.checkAndLoadFont(newModel.font, font);
        fontsController.checkAndLoadFont(newModel.hoverState.font, hoverState.font);
        fontsController.checkAndLoadFont(newModel.activeState.font, activeState.font);
    }

    public render():JSX.Element
    {
        const {
            type,
            isDisabled,
            iconLeft,
            iconRight,
            caption,
            captionVisibility,
            font,
            url,
            textAlign,
            textVerticalAlign,
            isNewWindow,
            background,
            border,
            boxShadow,
            hoverState,
            activeState,
            padding
        } = this.props.model;

        const iconName:IconName = BJSUtil.toBJSIconName(iconLeft);
        const rightIconName:IconName = BJSUtil.toBJSIconName(iconRight);
        const captionText:string = (caption && caption.value && captionVisibility.value) ? caption.value.toString() : null;
        const href:string = url && url.value && url.value.toString();
        const urlTarget:string = isNewWindow && LinkTarget.toTargetAttributeString(isNewWindow);

        let style:React.CSSProperties = {
            ...font.toCSS(),
            ...background.toCss(),
            ...border.toCSS(),
            boxShadow: boxShadow.toCssString()
        };

        const iconStyle:React.CSSProperties = {
            color: font.color.toCssString(),
            fontSize: font.size.toCssString(),
            opacity: 0.8
        };

        if(
            (this.state.isHovered || this.context.isParentHovered || this.props.propertyPanelSelectedTabId === PropertyPanelTabId.Hover)
            && hoverState.isEnabled.value
        )
        {
            const componentStyle:React.CSSProperties = this._getComponentStateStyle(hoverState);

            style = {...style, ...componentStyle};
            iconStyle.color = componentStyle.color;
            iconStyle.fontSize = componentStyle.fontSize;
        }

        if(
            (this.state.isActive || this.props.propertyPanelSelectedTabId === PropertyPanelTabId.Active)
            && activeState.isEnabled.value
        )
        {
            const componentStyle:React.CSSProperties = this._getComponentStateStyle(activeState);

            style = {...style, ...componentStyle};
            iconStyle.color = componentStyle.color;
            iconStyle.fontSize = componentStyle.fontSize;
        }

        const leftIcon:JSX.Element = <BJSIcon style={iconStyle} iconName={iconName}
                                              className={cx({onlyIcon: !captionText})} />;
        const rightIcon:JSX.Element = <BJSIcon style={iconStyle} iconName={rightIconName}
                                               className={cx(styles.rightIcon, {onlyIcon: !captionText})} />;

        const alignItems:any = TextVerticalAlign.toCssAlignItemsString(textVerticalAlign);
        const justifyContent:any = TextAlign.toCssJustifyContentString(textAlign);

        const btnStyle:React.CSSProperties = {
            justifyContent,
            alignItems,
            textAlign: textAlign.toString(),
            paddingTop: padding && padding.top && padding.top.toString(),
            paddingRight: padding && padding.right && padding.right.toString(),
            paddingBottom: padding && padding.bottom && padding.bottom.toString(),
            paddingLeft: padding && padding.left && padding.left.toString()
        };

        const isHorizontalCenter:boolean = TextAlign.isCenter(textAlign);
        const isVerticalCenter:boolean = TextVerticalAlign.isMiddle(textVerticalAlign);

        const isNotCentered = !isHorizontalCenter || !isVerticalCenter;

        const btn:JSX.Element = type.value.value === ButtonType.LINK_BUTTON.value ? (
            <AnchorButton
                href={href}
                target={urlTarget}
                rel={urlTarget === '_blank' ? 'noopener noreferrer' : 'noreferrer'}
                className={cx('BJSButton')}
                style={btnStyle}
            >
                {iconName && leftIcon}
                {captionText}
                {rightIconName && rightIcon}
            </AnchorButton>
        ) : (
            <BJSButton
                className={cx('BJSButton')}
            >
                <div
                    className={cx('btnContentWrapper', {isNotCentered})}
                    style={btnStyle}
                >
                    {iconName && leftIcon}
                    {captionText}
                    {rightIconName && rightIcon}
                </div>
            </BJSButton>
        );

        return (
            <div
                ref={this._refHandler}
                className={cx('base', {disabled: isDisabled.value})}
                style={style}
                onClick={this._onClick}
                onDoubleClick={this._onDoubleClick}
                onMouseDown={this._onMouseDown}
                onMouseUp={this._onMouseUp}
                onMouseMove={this._onMouseMove}
                onMouseOver={this._onMouseOver}
                onMouseOut={this._onMouseOut}
                onMouseEnter={this._onMouseEnter}
                onMouseLeave={this._onMouseLeave}
                onFocus={this._onFocused}
                onBlur={this._onLostFocus}
            >
                {btn}
            </div>
        );
    }

    private _getComponentStateStyle(state:ComponentState):React.CSSProperties
    {
        const {font, background, border, boxShadow} = state;

        return {
            ...font.toCSS(),
            ...background.toCss(),
            ...border.toCSS(),
            boxShadow: boxShadow.toCssString()
        };
    }

    @bind
    private _getPropStyler(propName:string):Styler
    {
        if( !this._buttonStyler && this._buttonRef )
            this._buttonStyler = styler(this._buttonRef, {preparseOutput: false});

        return this._buttonStyler;
    }

    @bind
    private _getPropPath(propName:string):string
    {
        switch( propName )
        {
            case AnimateProperty.BackgroundColor:
                return ButtonConverter.BG_SOLID_HEX;
            case AnimateProperty.BackgroundOpacity:
                return ButtonConverter.BG_SOLID_ALPHA;
            case AnimateProperty.BorderColor:
                return ButtonConverter.BORDER_COLOR_HEX;
            case AnimateProperty.BorderOpacity:
                return ButtonConverter.BORDER_COLOR_ALPHA;
            case AnimateProperty.Color:
                return ButtonConverter.COLOR_HEX;

            case AnimateRelatedProperty.BackgroundVisibility:
                return ButtonConverter.BG_SOLID_VISIBILITY;
            case AnimateRelatedProperty.BackgroundType:
                return ButtonConverter.BG_TYPE;
            case AnimateRelatedProperty.BorderVisibility:
                return ButtonConverter.BORDER_COLOR_VISIBILITY;
        }

        return '';
    }
}
